/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * A checkout that left for the gateway and has not come back yet.
 *
 * WHY THIS EXISTS. For a paid entry, nothing is written until the webhook fires
 * — see buildCheckoutPayload, which parks the rows inside the payment so an
 * abandoned checkout holds no seat. That is the right trade, but it leaves a
 * window: between pressing Pay and the webhook landing there is no registration
 * row AND no entered marker, so a runner who closes the return page and reopens
 * the event is offered Register again. Pressing it a second time is a second
 * payment for a place they may already have bought.
 *
 * This marker fills exactly that window, and nothing else.
 *
 * IT IS A HINT WITH AN EXPIRY, NEVER A FACT. `rememberEntered` says "you are
 * in"; this says only "you started paying and we have not heard the end of it".
 * The difference matters because the failure modes are opposite. An entered
 * marker that is wrong costs a redundant button. A PENDING marker that is wrong
 * hides the only button the page exists for — from somebody who abandoned the
 * payment at the gateway and holds no place at all. So this one is built to
 * let go:
 *
 *   • it carries the gateway order id, so the caller can ask the payments
 *     endpoint what actually happened and clear it on a real answer;
 *   • it expires on its own, for the case where that question can never be
 *     asked — the status endpoint 404s a payment it cannot match to the signed
 *     in buyer, which is precisely what happens when the session did not
 *     survive the redirect;
 *   • a marker with no order id is discarded on read, because nothing could
 *     ever resolve it.
 *
 * Same storage caveats as registrationMemory: per-browser, per-device, cleared
 * with site data, and never consulted to decide whether a write is allowed.
 */

const PREFIX = 'athlohive:paying';

/**
 * How long a started payment may go unanswered before the page stops mentioning
 * it.
 *
 * Long enough to cover a genuinely slow webhook and a runner who wandered off
 * mid-payment; short enough that somebody who abandoned the gateway is offered
 * their event back within the same sitting. It is a backstop, not the primary
 * mechanism — the status check clears the marker long before this on any
 * payment that actually resolves.
 *
 * TIED TO HOW LONG THE PLACE IS ACTUALLY HELD, which is what decided the
 * number. Checkout reserves the seat and gives it back when the hold expires,
 * so a marker outliving that would keep saying "registration in progress"
 * about a place the runner no longer has — hiding the Register button from
 * somebody free to use it. The two deadlines are the same deadline, and this
 * was thirty minutes while the hold is a quarter of an hour.
 *
 * SIXTEEN RATHER THAN THE TEN THE SERVER ASKS FOR, because ten is not what
 * the server actually gets. The hold is mirrored onto the payment gateway's
 * own order expiry, and Cashfree refuses to expire an order inside fifteen
 * minutes — so the effective hold is its floor, not our preference. See
 * CashfreeDriver.MIN_ORDER_LIFETIME_MINUTES. Matching the preference instead
 * of the floor would retire this marker while the place was still held.
 */
export const PENDING_TTL_MS = 16 * 60 * 1000;

/**
 * The words the EVENT page shows while a payment for it is unresolved.
 *
 * Deliberately not the return page's sentence. There the runner has just come
 * back from paying and the subject is the payment; here they are looking at an
 * event and the question in their head is "am I in or not", so the subject is
 * the registration. Both say the same underlying thing — it is not settled yet,
 * and an email is coming — without either claiming a place they may not hold.
 */
export const PENDING_TITLE = 'Registration in progress';
export const PENDING_BODY = 'Your registration for this event is still being '
  + 'confirmed. We will email you as soon as it is complete.';

/**
 * The words for the OTHER unresolved-payment state: somebody who already holds
 * a confirmed entry and has started a SECOND checkout for more places.
 *
 * WHY IT CANNOT REUSE THE PAIR ABOVE. That one says the registration for this
 * event is still being confirmed. Said to somebody who is already in, it
 * contradicts the "You are registered" line directly above it and puts a
 * settled place in doubt — the one thing this file exists to avoid. Here the
 * entry is not in question at all; the new seats are.
 *
 * IT CLAIMS NOTHING ABOUT THE OUTCOME, in either direction. Not that the
 * places are held, not that they are lost, not that the money is safe — only
 * that a payment was started, that the answer is not in yet, and that this is
 * why a second checkout is not on offer. The buyer is told what to expect
 * (an email) rather than what to do, because the obvious instruction — try
 * again — is how somebody pays twice for one seat.
 *
 * Without this, "Book more seats" simply vanished from a card that had just
 * been showing it, with nothing said. Hiding it is right; saying nothing is
 * what made it look broken.
 */
export const PENDING_MORE_TITLE = 'Previous request in progress';
export const PENDING_MORE_BODY = 'Your previous registration request is still '
  + 'being confirmed. Please wait a short while — we will email you as soon as '
  + 'it is settled. If it does not go through, you can book again.';

/**
 * The phone dock's version, and the whole of it: one line, standing alone.
 *
 * The dock is a single row on a phone, and this state already costs it a row
 * it did not have. A heading with a second line under it would take a third,
 * pushing the entry line and the pass button further from the thumb. The full
 * sentence is the rail's job on a wider screen.
 */
export const PENDING_MORE_BAR = 'Awaiting payment confirmation';

/** Namespaced per application, so two apps on one origin cannot collide. */
function keyFor(organizationId, applicationId, eventId) {
  return `${PREFIX}:${organizationId}:${applicationId}:${eventId}`;
}

/**
 * Records that a payment for this event has been started and not yet resolved.
 *
 * Silent on failure, like every other write here: a private window or a full
 * quota is not a reason to stop somebody paying.
 */
export function rememberPending(organizationId, applicationId, eventId, { orderId, userId } = {}) {
  if (!orderId) return;
  try {
    window.localStorage.setItem(
      keyFor(organizationId, applicationId, eventId),
      JSON.stringify({
        orderId: String(orderId),
        at: Date.now(),
        userId: userId === undefined || userId === null ? null : Number(userId),
      }),
    );
  } catch (e) {
    // Deliberately silent — see above.
  }
}

/**
 * The unresolved payment for this event, or null.
 *
 * Null for anything that cannot be acted on: no marker, a shape we did not
 * write, no order id to check, or one that has sat here past its welcome.
 */
export function recallPending(organizationId, applicationId, eventId) {
  try {
    const raw = window.localStorage.getItem(keyFor(organizationId, applicationId, eventId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    // Nothing to ask the payments endpoint about, so nothing that could ever
    // clear it. Treated as absent rather than left to expire on the clock.
    if (!parsed.orderId) return null;

    const at = Number(parsed.at);
    if (!Number.isFinite(at) || Date.now() - at > PENDING_TTL_MS) return null;

    return {
      orderId: String(parsed.orderId),
      at,
      userId: parsed.userId === undefined || parsed.userId === null
        ? null : Number(parsed.userId),
    };
  } catch (e) {
    return null;
  }
}

/** Forgets it — on a resolved payment, either way it resolved. */
export function forgetPending(organizationId, applicationId, eventId) {
  try {
    window.localStorage.removeItem(keyFor(organizationId, applicationId, eventId));
  } catch (e) {
    // Silent, as above.
  }
}

/**
 * Whether a status reply settles a noted payment, and which way.
 *
 * Returns 'confirmed', 'failed', or null for "still unresolved / cannot tell".
 *
 * WHY THIS LIVES HERE and not on the return page. That page's `phaseFor` maps a
 * reply onto five screens; this asks the one question the EVENT page has, which
 * is whether the note may be torn up. Keeping it separate means the event page
 * does not import a page module — and pending-payment.test.js pins the two
 * against each other on every terminal case, so they cannot drift apart.
 *
 * Null on an unreadable or missing reply is deliberate: the note then survives
 * to its expiry rather than being cleared on a question that was never
 * answered.
 */
export function settlementOf(status) {
  if (!status) return null;
  if (status.status === 'paid') return status.fulfilled ? 'confirmed' : null;
  if (status.status === 'cancelled' || status.status === 'other') return 'failed';
  if (status.gateway_status === 'payment_failed') return 'failed';
  return null;
}

/**
 * Forgets every noted payment in this browser. Called on sign-out.
 *
 * WHY SIGN-OUT NEEDS THIS AT ALL, given every read is already checked against
 * whoever is signed in. That check governs what is SHOWN; it does not remove
 * anything until the next visit to the event in question. So without this, a
 * note survives the logout on disk — an order id and a user id, readable by the
 * next person to open devtools — and one that expires is only ever ignored on
 * read, never deleted, so the keys accumulate for good.
 *
 * It sweeps its own prefix and nothing else. The entered markers belong to
 * registrationMemory and guard themselves; reaching into them from here would
 * be owning a mechanism this file did not write.
 *
 * Keys are collected before anything is removed — deleting inside the loop
 * shifts the indices under it and silently skips half of them.
 */
export function forgetAllPending() {
  try {
    const doomed = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(`${PREFIX}:`)) doomed.push(key);
    }
    doomed.forEach((key) => window.localStorage.removeItem(key));
  } catch (e) {
    // Silent, and deliberately so: a browser that will not let us tidy up is
    // not a reason to fail the sign-out the runner actually asked for.
  }
}
